import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import TriangleToggle from 'config-migration/shared-ui-components/TriangleToggle';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY, GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';

import {BenchlingAppActivitySessionsQuery} from './__generated/BenchlingAppActivitySessionsQuery';
import BenchlingAppSessionActivityMessagesTable from './BenchlingAppSessionActivityMessagesTable';
import BenchlingAppSessionStatusCell from './BenchlingAppSessionStatusCell';

type BenchlingAppSessionNode = ExtractByTypename<
  BenchlingAppActivitySessionsQuery['benchlingApp'],
  'BenchlingAppNode'
>['sessionListing']['sessions'][number];

export interface SessionActivityRowsProps {
  sessions: Array<BenchlingAppSessionNode>;
  areRowsCollapsed: Map<string, boolean>;
  isCollapsedCallback: (sessionId: string, isCollapsed: boolean) => void;
  dataTestAttrs: DataTestAttrs;
}

// Returns a <tbody/> containing all the rows corresponding to all the sessions.
// Each input session has a toggleTriangle with a collapsed/opened state.
// Each input session may return one row (if collapsed) or two rows (if opened).
export default function BenchlingAppSessionActivityTableRows(props: SessionActivityRowsProps): JSX.Element {
  const rows = [];
  for (const session of props.sessions) {
    const isCollapsed: boolean = props.areRowsCollapsed.get(session.id) ?? true;
    const setIsCollapsed = (value: boolean): void => {
      props.isCollapsedCallback(session.id, value);
    };
    // each session might generate one row (if closed) or two rows (if opened)
    for (const row of generateRows(session, isCollapsed, setIsCollapsed)) {
      rows.push(row);
    }
  }
  return <tbody {...getTestProps(props.dataTestAttrs)}>{...rows}</tbody>;
}

// Generator: each session might generate one row (if closed) or two rows (if opened)
function* generateRows(
  session: BenchlingAppSessionNode,
  isCollapsed: boolean,
  setIsCollapsed: (isCollapsed: boolean) => void
): Generator<JSX.Element> {
  // Each row must have a unique key. See https://reactjs.org/link/warning-keys for more information.
  const key = session.id;
  yield (
    <tr key={key} className={css(styles.sessionRow, isCollapsed ? styles.sessionRowClosed : null)}>
      <td className={css(styles.triangleToggle)}>
        <TriangleToggle toggleLabel="" isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed}>
          <></>
        </TriangleToggle>
      </td>
      <td className={css(styles.statusCell)}>
        <BenchlingAppSessionStatusCell
          status={session.status}
          dataTestAttrs={{component: 'SessionStatusCell', key}}
        />
      </td>
      <td className={css(styles.nameCell)} {...getTestProps({component: 'SessionNameCell', element: 'root'})}>
        {session.name}
      </td>
      <td className={css(styles.timeCell)}>
        <DatetimeTableCell datetime={session.modifiedAt} showTimeInCell={true} />
      </td>
    </tr>
  );
  // Below the first row, there is an optional second row displaying the session's messages.
  if (!isCollapsed) {
    yield <BenchlingAppSessionActivityMessagesTable key={`${key}_msgs`} session={session} />;
  }
}

export const styles = StyleSheet.create({
  sessionRow: {
    height: '32px',
    borderCollapse: 'separate',
    ':hover': {
      background: GRAY_LIGHTER,
    },
    ':hover + tr.messageRow': {
      backgroundColor: GRAY_LIGHTER,
    },
    ':hover + tr.messageRow div.messagesCard': {
      backgroundColor: GRAY_LIGHT,
      border: `1px solid ${GRAY}`,
    },
  },
  sessionRowClosed: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  triangleToggle: {
    padding: '0px 12px',
  },
  statusCell: {
    paddingLeft: '0px',
    width: '20%',
  },
  nameCell: {
    paddingLeft: '0px',
    width: '45%',
  },
  timeCell: {
    paddingLeft: '0px',
    width: '35%',
  },
});
